import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { useAudioPlayer, Stream } from '../contexts/AudioPlayerContext';
import socialService, { FavoriteWithStream } from '../services/social';
import { logger } from '../utils/logger';
import './Favorites.css';

const Favorites: React.FC = () => {
  const { playStream, addToQueue } = useAudioPlayer();
  const [favorites, setFavorites] = useState<FavoriteWithStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFavorites = useCallback(async (token?: string) => {
    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await socialService.getUserFavorites(20, token);

      if (token) {
        setFavorites(prev => [...prev, ...response.favorites]);
      } else {
        setFavorites(response.favorites);
      }
      setNextToken(response.nextToken);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load favorites';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleLoadMore = () => {
    if (nextToken && !loadingMore) {
      loadFavorites(nextToken);
    }
  };

  const handlePlay = (e: React.MouseEvent, favorite: FavoriteWithStream) => {
    e.preventDefault();
    e.stopPropagation();
    if (favorite.stream?.audioUrl) {
      const stream: Stream = {
        streamId: favorite.streamId,
        name: favorite.stream.name,
        description: favorite.stream.description,
        audioUrl: favorite.stream.audioUrl,
        coverImage: favorite.stream.coverImage,
        uploaderUsername: favorite.stream.uploaderUsername,
        uploaderDisplayName: favorite.stream.uploaderDisplayName,
      };
      playStream(stream);
    }
  };

  const handleAddToQueue = (e: React.MouseEvent, favorite: FavoriteWithStream) => {
    e.preventDefault();
    e.stopPropagation();
    if (favorite.stream?.audioUrl) {
      const stream: Stream = {
        streamId: favorite.streamId,
        name: favorite.stream.name,
        description: favorite.stream.description,
        audioUrl: favorite.stream.audioUrl,
        coverImage: favorite.stream.coverImage,
        uploaderUsername: favorite.stream.uploaderUsername,
        uploaderDisplayName: favorite.stream.uploaderDisplayName,
      };
      addToQueue(stream);
    }
  };

  const handleRemoveFavorite = async (e: React.MouseEvent, streamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await socialService.removeFavorite(streamId);
      setFavorites(prev => prev.filter(f => f.streamId !== streamId));
    } catch (err) {
      logger.error('Failed to remove favorite', err);
    }
  };

  if (loading) {
    return (
      <div className="favorites-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading your favorites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="favorites-page">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => loadFavorites()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <header className="page-header">
        <h1>Your Favorites</h1>
        <p className="subtitle">Streams you've favorited</p>
      </header>

      {favorites.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">❤️</div>
          <h3>No favorites yet</h3>
          <p>Favorite streams to save them here for easy access.</p>
          <Link to="/streams" className="btn btn-primary">
            Browse Streams
          </Link>
        </div>
      ) : (
        <>
          <div className="favorites-grid">
            {favorites.map((favorite) => (
              <div key={favorite.favoriteId} className="favorite-card">
                <Link
                  to={`/streams/${favorite.streamId}`}
                  className="favorite-link"
                >
                  <div className="favorite-cover">
                    {favorite.stream?.coverImage ? (
                      <img
                        src={favorite.stream.coverImage}
                        alt={favorite.stream?.name || 'Stream'}
                      />
                    ) : (
                      <div className="cover-placeholder">
                        <span>🎵</span>
                      </div>
                    )}
                    <div className="favorite-overlay">
                      <button
                        className="play-button"
                        onClick={(e) => handlePlay(e, favorite)}
                        aria-label="Play"
                      >
                        ▶
                      </button>
                    </div>
                  </div>
                  <div className="favorite-info">
                    <h3 className="favorite-name">
                      {favorite.stream?.name || 'Unknown Stream'}
                    </h3>
                    {favorite.stream?.uploaderUsername && (
                      <p className="favorite-uploader">
                        by @{favorite.stream.uploaderUsername}
                      </p>
                    )}
                  </div>
                </Link>
                <div className="favorite-actions">
                  <button
                    className="action-button"
                    onClick={(e) => handleAddToQueue(e, favorite)}
                    title="Add to queue"
                  >
                    +
                  </button>
                  <button
                    className="action-button remove"
                    onClick={(e) => handleRemoveFavorite(e, favorite.streamId)}
                    title="Remove from favorites"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          {nextToken && (
            <div className="load-more-container">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-secondary"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Favorites;
